import { Component, Element, Host, Prop, h } from '@stencil/core';
import FieldDecorator from '../_interfaces/FieldDecorator';
import warn from '../../../../capabilities/warn';

/**
 * @description form-kit: Applies Design System styles to a slotted checkbox field.Applies Design System styles to a slotted select tag.
 *
 * @version 4
 * @use
 * @formkit
 * @since 4.1.0
 * @slot - `default slot`: The select element to be decorated.
 */
@Component({
  tag: 'hp6-select-decorator',
  styleUrl: 'select-decorator.scss',
  shadow: true,
})
export class SelectDecorator implements FieldDecorator {
  /**
   * The height of the element.
   */
  @Prop() heightSelection: 'md' | 'sm' = 'md';

  /**
   * Represents the validity status of the decorated input. Undefined indicates the user has not yet accessed it.
   */
  @Prop({ reflect: true }) isValid: boolean = undefined;

  /**
   * Boolean that indicates whether to add an initial default option to the list of options.
   * The initial default option is not selectable. The text for this option is "— Select an option —".
   * Use the property initialDefaultOptionText to override this default text.
   */
  @Prop() addInitialDefaultOption: boolean = false;

  /**
   * String used to create an initial default option with the given text in the list of options.
   * This initial default option is not selectable. A non-blank string in this property will add
   * an initial default option to the list of options regardless of the value of the
   * addInitialDefaultOption property.
   */
  @Prop() initialDefaultOptionText: string = 'Select an option';

  @Element() host: HTMLElement;

  get decoratedEl() {
    return this.host.querySelector('select');
  }

  decorate() {
    const { decoratedEl } = this;

    if (!decoratedEl) {
      warn('hp6-select-decorator select not found');
      return;
    }

    const hasId = decoratedEl.getAttribute('id');
    if (!hasId) {
      warn(
        'select-decorator: select does not have an id attribute. This is required to associate the label with the select.',
      );
    }

    const selectOption = decoratedEl.querySelector('option[data-default-option]');

    if (this.addInitialDefaultOption && !selectOption) {
      const option = document.createElement('option');
      option.setAttribute('data-default-option', '');
      option.value = '';
      option.disabled = true;
      /* If selectedIndex = 0, we don't know if the user chose that option, or if the select
        defaulted to the first iteam without user choice. If it is >= 1 it is definitely a user selection  */
      if (decoratedEl.selectedIndex < 1) {
        option.selected = true;
      }
      // Note these are special "em dash" characters, not regular dashes
      option.textContent = `— ${this.initialDefaultOptionText} —`;
      decoratedEl.insertBefore(option, decoratedEl.firstChild);
    }
    if (!this.addInitialDefaultOption && selectOption) {
      selectOption.remove();
    }
  }

  componentDidRender() {
    this.decorate();
  }

  render() {
    const { heightSelection, isValid } = this;

    const isValidated = isValid !== undefined;
    const validClass = isValid ? 'valid' : 'invalid';

    return (
      <Host data-decorator="select" class={isValidated ? validClass : ''}>
        <div
          class={`select-wrapper ${heightSelection ? `height-selection-${heightSelection}` : ''}`}
        >
          <slot />
          <hp6-icon name="system-arrow-down" size-selection="sm" />
          {isValidated && (
            <hp6-icon
              class="validation-icon"
              size-selection="sm"
              name={isValid ? 'system-success' : 'system-error'}
            />
          )}
        </div>
      </Host>
    );
  }
}
